import { config } from '@/config'
import { userManager, isTokenExpired } from '@/auth/userManager'
import { currentLanguage } from '@/i18n'

export class ApiError extends Error {
  // Plain fields rather than constructor parameter properties: the tsconfig runs with
  // `erasableSyntaxOnly`, which rules out syntax that emits code.
  readonly status: number
  /**
   * The `detail` of an RFC 9457 problem document, when the backend sent one. Present exactly
   * where the message is written for a person to read — "this is the only remaining
   * administrator" — and absent otherwise, so a caller can tell "show this" from "say something
   * generic" without matching on strings.
   */
  readonly detail: string | null
  /**
   * The machine-readable reason, e.g. `LAST_ADMIN`. The backend's `detail` is English while this
   * UI is German first, so the code is what gets translated and the detail is only the fallback.
   */
  readonly code: string | null

  constructor(status: number, message: string, problem: Problem = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = problem.detail ?? null
    this.code = problem.code ?? null
  }
}

type Problem = { detail?: string | null; code?: string | null }

/**
 * `RestExceptionHandler` answers with `application/problem+json`, and only ever puts a message
 * meant for the caller in `detail` — everything unexpected is a fixed, contentless sentence. So
 * reading it here cannot leak an internal error text into the UI.
 */
async function problem(response: Response): Promise<Problem> {
  if (!response.headers.get('Content-Type')?.includes('json')) {
    return {}
  }
  try {
    const body = (await response.json()) as { detail?: unknown; code?: unknown } | null
    return { detail: text(body?.detail), code: text(body?.code) }
  } catch {
    // A body that is not the JSON it claimed to be changes nothing about the status code.
    return {}
  }
}

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null

/**
 * oidc-client-ts renews in the background too, but a suspended tab misses those timers — hence
 * the check at the point of use.
 */
async function accessToken(): Promise<string | null> {
  const user = await userManager.getUser()
  if (!user) {
    return null
  }
  if (!isTokenExpired(user.access_token)) {
    return user.access_token
  }
  try {
    const renewed = await userManager.signinSilent()
    return renewed?.access_token ?? null
  } catch {
    return null
  }
}

/**
 * On 401 the local session is dropped, so the app falls back to the login screen instead of
 * retrying with a token the backend will keep refusing.
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await accessToken()
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  // So backend validation messages and error texts come back in the language the UI is showing.
  headers.set('Accept-Language', currentLanguage())
  // Not for FormData: the browser sets `multipart/form-data` itself, with the boundary, and a
  // hand-written header would produce a body the server cannot parse.
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const response = await fetch(`${config.apiUrl}${path}`, { ...init, headers })

  if (response.status === 401) {
    await userManager.removeUser()
    throw new ApiError(401, 'Not authenticated')
  }
  if (!response.ok) {
    throw new ApiError(
      response.status,
      `Request to ${path} failed with ${response.status}`,
      await problem(response),
    )
  }
  // Not just 204: `POST /api/auth/forgot-password` answers 202 with no body at all, and calling
  // `json()` on one throws.
  if (response.status === 204 || !response.headers.get('Content-Type')?.includes('json')) {
    return undefined as T
  }
  return (await response.json()) as T
}

/**
 * The same request, for a response that is not JSON.
 *
 * Images live behind the lead's visibility rule, so they are served by the API with a bearer token
 * like everything else — which is exactly what a plain `<img src>` cannot send. The bytes therefore
 * come through here and are handed to the browser as an object URL.
 */
/**
 * A downloaded file and the name the server gave it.
 *
 * The name matters: the export is stamped with the day it was taken, and a client that invents its
 * own would overwrite yesterday's download with today's under one filename.
 */
export type DownloadedFile = { blob: Blob; filename: string | null }

export async function apiDownload(path: string, init: RequestInit = {}): Promise<DownloadedFile> {
  const response = await blobResponse(path, init)
  return { blob: await response.blob(), filename: filenameFrom(response) }
}

/** RFC 6266. `filename*` first, because that is the one that survives a non-ASCII name. */
function filenameFrom(response: Response): string | null {
  const header = response.headers.get('Content-Disposition')
  if (!header) {
    return null
  }
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1])
    } catch {
      // A malformed header is not worth failing a download over; fall through to the plain name.
    }
  }
  return /filename="?([^";]+)"?/i.exec(header)?.[1] ?? null
}

export async function apiFetchBlob(path: string, init: RequestInit = {}): Promise<Blob> {
  return (await blobResponse(path, init)).blob()
}

async function blobResponse(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await accessToken()
  const headers = new Headers(init.headers)
  headers.set('Accept-Language', currentLanguage())
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const response = await fetch(`${config.apiUrl}${path}`, { ...init, headers })

  if (response.status === 401) {
    await userManager.removeUser()
    throw new ApiError(401, 'Not authenticated')
  }
  if (!response.ok) {
    // The same problem+json the JSON path reads. A failed image fetch carries a `code` too —
    // FILE_NOT_FOUND, for one — and dropping it here would make its translation unreachable.
    throw new ApiError(
      response.status,
      `Request to ${path} failed with ${response.status}`,
      await problem(response),
    )
  }
  return response
}

/**
 * A multipart upload. `Content-Type` is deliberately **not** set: the browser has to add it itself
 * so that it carries the boundary, and setting it by hand produces a body the server cannot parse.
 */
export async function apiUpload<T>(path: string, file: File): Promise<T> {
  const body = new FormData()
  body.append('file', file)
  return apiFetch<T>(path, { method: 'POST', body })
}
